"use client"

import { useState, useEffect } from "react"
import { motion } from "framer-motion"
import { HelpCircle, Sparkles } from "lucide-react"
import MetaTags from "../components/SEO/MetaTags"
import { pageConfigs } from "../utils/seo"

// FOWND General FAQs
const fowndFAQs = [
  {
    question: "What is FOWND?",
    answer: "FOWND is a company built by physical therapists to solve real clinical problems using AI—starting with documentation."
  },
  {
    question: "Who is FOWND for?",
    answer: "FOWND is designed for outpatient physical therapy clinics of all sizes—from single providers to enterprise-level groups."
  },
  {
    question: "What makes FOWND different?",
    answer: "We're PTs ourselves. We understand the unique needs of rehab therapy and build tools that reflect clinical nuance and real-world workflow."
  },
  {
    question: "Is FOWND only for physical therapy?",
    answer: "Our first tool, Notation, is purpose-built for physical therapists. We're exploring future support for OT and SLP."
  },
  {
    question: "Is FOWND HIPAA-compliant?",
    answer: "Yes. FOWND fully complies with HIPAA regulations to ensure your clinical data and patient records remain secure and protected."
  },
  {
    question: "Can I customize my SOAP note templates?",
    answer: "Absolutely. You can easily customize SOAP note templates to fit your clinic's unique documentation workflows and preferences."
  },
  {
    question: "How does our AI medical scribe work?",
    answer: "FOWND creates real-time SOAP notes built for PT—goals, HEPs, codes, and more—directly in your browser, no installs or EMR integration required. Therapists shape notes as they’re drafted, while FOWND adapts to your clinic’s workflow, improving speed, accuracy, and ease with every session."
  },
  {
    question: "Works for solo PTs and larger clinics?",
    answer: "Both. Whether you're a solo practitioner or a multi-provider clinic, FOWND scales to your needs and helps streamline documentation."
  }
]

// Notation Specific FAQs
const notationFAQs = [
  {
    question: "What is Notation?",
    answer: "A real-time AI assistant that listens to your sessions and auto-generates compliant SOAP notes."
  },
  {
    question: "How does Notation work?",
    answer: "Uses ambient AI and natural language processing to capture clinically relevant information and draft SOAP notes instantly."
  },
  {
    question: "Does it work with our EMR?",
    answer: "Yes, we have a proprietary plugin that transfers all information generated by Notation to your EMR."
  },
  {
    question: "What do I need to use it?",
    answer: "Just a device with a microphone and internet connection. No special hardware required."
  },
  {
    question: "Will this replace therapists?",
    answer: "Never. Notation supports clinicians by handling documentation tasks, not clinical judgment or care."
  },
  {
    question: "How do I get started?",
    answer: "Click 'Get Started' or 'Schedule a Demo'. Our team will walk you through setup and training."
  },
  {
    question: "How long is onboarding?",
    answer: "Onboarding is instant—just create an account and you're ready to start using Notation."
  },
  {
    question: "Is there a free trial?",
    answer: "Yes! We offer a free trial so you can see Notation in action in your clinic before committing."
  },
  {
    question: "How much does it cost?",
    answer: "We offer flexible pricing based on clinic size and usage. Check our pricing section on the Notation page."
  },
  {
    question: "Are there enterprise discounts?",
    answer: "Yes. We offer volume discounts and enterprise onboarding for larger organizations."
  },
  {
    question: "Works with low Wi-Fi?",
    answer: "Notation is lightweight and cloud-based. We'll help you test compatibility before you commit."
  },
  {
    question: "What happens to session data?",
    answer: "Session data is securely stored and accessible only to authorized users. Export, review, or delete anytime."
  },
  {
    question: "Can I test it myself first?",
    answer: "Absolutely. We encourage therapists to experience Notation firsthand—no pressure to commit."
  },
  {
    question: "Can I use it individually?",
    answer: "Yes, you can use Notation individually as a PT without your clinic needing to sign up."
  }
]

export default function FAQ() {
  const [scrollY, setScrollY] = useState(0)

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY)
    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  return (
    <>
      <MetaTags
        title={pageConfigs.faq.title}
        description={pageConfigs.faq.description}
        keywords={pageConfigs.faq.keywords}
        canonical={pageConfigs.faq.canonical}
      />
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        {/* Hero Section */}
        <section className="relative overflow-hidden bg-gradient-to-br from-[#1e3d3b] via-[#2a5a56] to-[#1e3d3b]">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-transparent via-[#1e3d3b]/20 to-[#1e3d3b]/40"></div>
          <div
            className="relative px-6 py-24 md:py-32 lg:py-40"
            style={{
              transform: `translateY(${scrollY * 0.3}px)`,
            }}
          >
            <div className="mx-auto max-w-4xl text-center">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight font-productSans">
                Frequently Asked{" "}
                <span className="bg-white text-[#1e3d3b] px-4 py-2 rounded-lg inline-block mt-2">
                  Questions
                </span>
              </h1>
              <p className="text-lg md:text-xl text-slate-200 max-w-3xl mx-auto leading-relaxed">
                Everything you need to know about FOWND and Notation
              </p>
            </div>
          </div>

          {/* Decorative elements */}
          <div className="absolute top-20 left-10 w-20 h-20 bg-white/5 rounded-full blur-xl"></div>
          <div className="absolute bottom-20 right-10 w-32 h-32 bg-white/5 rounded-full blur-xl"></div>
        </section>

        {/* FOWND General FAQs Section */}
        <section className="py-20 px-6">
          <div className="mx-auto max-w-7xl">
            <div className="text-center mb-16">
              <div className="inline-flex items-center text-[#1e3d3b] text-base mb-6 font-semibold">
                <Sparkles className="w-4 h-4 mr-2" />
                General Questions
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">
                About FOWND
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                Learn about our company, mission, and core values
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {fowndFAQs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-slate-200 hover:border-[#1e3d3b]/30 transition-all duration-300 hover:shadow-lg group"
                >
                  <h3 className="text-slate-800 font-semibold text-lg mb-3 font-productSans group-hover:text-[#1e3d3b] transition-colors duration-300">
                    {faq.question}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    {faq.answer}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Notation Specific FAQs Section */}
        <section className="py-20 px-6 bg-white/50">
          <div className="mx-auto max-w-7xl">
            <div className="text-center mb-16">
              <div className="inline-flex items-center text-[#619ed3] text-base mb-6 font-semibold">
                <Sparkles className="w-4 h-4 mr-2" />
                Product Questions
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">
                About Notation
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                Everything about our AI-powered clinical documentation platform
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {notationFAQs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-slate-200 hover:border-[#619ed3]/30 transition-all duration-300 hover:shadow-lg group"
                >
                  <h3 className="text-slate-800 font-semibold text-lg mb-3 font-productSans group-hover:text-[#619ed3] transition-colors duration-300">
                    {faq.question}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    {faq.answer}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Call to Action Section */}
        <section className="py-20 bg-gradient-to-r from-[#1e3d3b] to-[#2a5a56]">
          <div className="mx-auto max-w-4xl text-center px-6">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Still have questions?
            </h2>
            <p className="text-xl text-emerald-50 mb-8 max-w-2xl mx-auto">
              Our team is here to help. Schedule a call or start a conversation with us today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href="https://bookings.cloud.microsoft/book/NotationAI@fownd.org/?ismsaljsauthenabled"
                target="_blank"
                rel="noopener noreferrer"
              >
                <button className="bg-white text-[#1e3d3b] hover:bg-slate-100 font-semibold px-8 py-3 rounded-full transition-all duration-300 transform hover:scale-105">
                  Schedule a Demo
                </button>
              </a>
              <a
                href="https://notation.fownd.care/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <button className="border-2 border-white text-white hover:bg-white/10 font-semibold px-8 py-3 rounded-full transition-all duration-300 transform hover:scale-105">
                  Try Notation Free
                </button>
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  )
} 